import { useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Search as SearchIcon, MapPin, Minus, Plus, Navigation } from "lucide-react";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { month: "short", day: "numeric" }) : "";

const SUGGESTED = [
  { city: "", label: "Nearby", desc: "Find what's around you", nearby: true },
  { city: "Goa", label: "North Goa, Goa", desc: "Popular beach destination" },
  { city: "Pune", label: "Pune, Maharashtra", desc: "A hidden gem" },
  { city: "Goa", label: "Candolim, Goa", desc: "Near you" },
  { city: "Mumbai", label: "Mumbai, Maharashtra", desc: "For sights like Gateway of India" },
  { city: "Bengaluru", label: "Bengaluru, Karnataka", desc: "For its top-notch dining" },
  { city: "Goa", label: "Siolim, Goa", desc: "Near you" },
];

const FLEX_CHIPS = ["Exact dates", "± 1 day", "± 2 days", "± 3 days", "± 7 days", "± 14 days"];

function StepperRow({ title, subtitle, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between py-3">
      <div>
        <div className="text-sm font-semibold text-[#222]">{title}</div>
        <div className="text-xs text-[#717171]">{subtitle}</div>
      </div>
      <div className="flex items-center gap-3">
        <button type="button" data-testid={`${testid}-minus`} onClick={() => onChange(Math.max(0, value - 1))} disabled={value <= 0} className="w-8 h-8 rounded-full border border-[#B0B0B0] flex items-center justify-center disabled:opacity-40 hover:border-[#222] transition-colors"><Minus className="w-4 h-4" /></button>
        <span data-testid={`${testid}-count`} className="w-5 text-center text-sm font-medium">{value}</span>
        <button type="button" data-testid={`${testid}-plus`} onClick={() => onChange(value + 1)} className="w-8 h-8 rounded-full border border-[#B0B0B0] flex items-center justify-center hover:border-[#222] transition-colors"><Plus className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

const toISO = (d) => d ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10) : "";

export function SearchBar() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [where, setWhere] = useState(sp.get("q") || sp.get("city") || "");
  const [whereOpen, setWhereOpen] = useState(false);
  const [dateMode, setDateMode] = useState("dates");
  const [dateRange, setDateRange] = useState({
    from: sp.get("check_in") ? new Date(sp.get("check_in")) : undefined,
    to: sp.get("check_out") ? new Date(sp.get("check_out")) : undefined,
  });
  const [flex, setFlex] = useState(sp.get("flex") || "Exact dates");
  const [adults, setAdults] = useState(Number(sp.get("adults")) || 0);
  const [children, setChildren] = useState(Number(sp.get("children")) || 0);
  const [infants, setInfants] = useState(Number(sp.get("infants")) || 0);
  const [pets, setPets] = useState(Number(sp.get("pets")) || 0);

  const totalGuests = adults + children;
  const disabledDates = useMemo(() => ({ before: new Date() }), []);
  const whenLabel = dateRange?.from ? `${fmtDate(dateRange.from)}${dateRange.to ? ` – ${fmtDate(dateRange.to)}` : ""}` : "Add dates";
  const whoLabel = (() => {
    const parts = [];
    if (totalGuests > 0) parts.push(`${totalGuests} guest${totalGuests > 1 ? "s" : ""}`);
    if (infants > 0) parts.push(`${infants} infant${infants > 1 ? "s" : ""}`);
    if (pets > 0) parts.push(`${pets} pet${pets > 1 ? "s" : ""}`);
    return parts.length ? parts.join(", ") : "Add guests";
  })();

  const onSubmit = (e) => {
    e?.preventDefault?.();
    const ns = new URLSearchParams(sp.toString());
    ns.delete("city");
    const setOrDel = (k, v) => { if (v) ns.set(k, String(v)); else ns.delete(k); };
    setOrDel("q", where);
    setOrDel("check_in", dateRange?.from ? toISO(dateRange.from) : "");
    setOrDel("check_out", dateRange?.to ? toISO(dateRange.to) : "");
    setOrDel("flex", flex !== "Exact dates" ? flex : "");
    setOrDel("adults", adults || "");
    setOrDel("children", children || "");
    setOrDel("infants", infants || "");
    setOrDel("pets", pets || "");
    setOrDel("guests", totalGuests || "");
    navigate(`/search?${ns.toString()}`);
  };

  return (
    <form onSubmit={onSubmit}>
      <div className="flex flex-col md:flex-row md:items-center bg-white rounded-3xl md:rounded-full border border-[#DDDDDD] shadow-[0_3px_14px_rgba(0,0,0,0.10)] md:pr-2">
        {/* Where */}
        <Popover open={whereOpen} onOpenChange={setWhereOpen}>
          <PopoverTrigger asChild>
            <label className="flex-1 cursor-text px-6 py-3 rounded-full hover:bg-[#F7F7F7] transition-colors block">
              <div className="text-xs font-semibold text-[#222]">Where</div>
              <input
                data-testid="search-where-input"
                value={where}
                onChange={(e) => { setWhere(e.target.value); setWhereOpen(true); }}
                placeholder="Search destinations"
                className="w-full bg-transparent outline-none text-sm text-[#222] placeholder:text-[#717171] mt-0.5"
              />
            </label>
          </PopoverTrigger>
          <PopoverContent align="start" onOpenAutoFocus={(e) => e.preventDefault()} className="w-80 rounded-3xl p-2 shadow-xl">
            <div className="text-xs font-semibold text-[#717171] px-3 pt-2 pb-1">Suggested destinations</div>
            <div className="max-h-72 overflow-y-auto">
              {SUGGESTED.map((s, i) => (
                <button key={`${s.label}-${i}`} type="button" data-testid={`where-suggestion-${i}`} onClick={() => { setWhere(s.city); setWhereOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2 rounded-2xl hover:bg-[#F7F7F7] text-left transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-[#F0F0F4] flex items-center justify-center shrink-0">
                    {s.nearby ? <Navigation className="w-4 h-4 text-[#2f80ed]" /> : <MapPin className="w-4 h-4 text-[#717171]" />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#222] truncate">{s.label}</div>
                    <div className="text-xs text-[#717171] truncate">{s.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <div className="hidden md:block h-8 w-px bg-[#DDDDDD]" />

        {/* When */}
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" data-testid="search-when-trigger" className="flex-1 text-left px-6 py-3 rounded-full hover:bg-[#F7F7F7] transition-colors border-t border-[#EBEBEB] md:border-t-0">
              <div className="text-xs font-semibold text-[#222]">When</div>
              <div data-testid="search-when-value" className={`text-sm mt-0.5 truncate ${dateRange?.from ? "text-[#222]" : "text-[#717171]"}`}>{whenLabel}</div>
            </button>
          </PopoverTrigger>
          <PopoverContent align="center" className="w-auto max-w-[95vw] rounded-3xl p-4 shadow-xl">
            <div className="flex justify-center mb-3">
              <div className="inline-flex bg-[#EBEBEB] rounded-full p-1">
                <button type="button" data-testid="when-tab-dates" onClick={() => setDateMode("dates")} className={`px-5 py-1.5 rounded-full text-sm transition-colors ${dateMode === "dates" ? "bg-white shadow font-medium text-[#222]" : "text-[#717171]"}`}>Dates</button>
                <button type="button" data-testid="when-tab-flexible" onClick={() => setDateMode("flexible")} className={`px-5 py-1.5 rounded-full text-sm transition-colors ${dateMode === "flexible" ? "bg-white shadow font-medium text-[#222]" : "text-[#717171]"}`}>Flexible</button>
              </div>
            </div>
            <Calendar mode="range" numberOfMonths={2} selected={dateRange} onSelect={(r) => setDateRange(r || {})} disabled={disabledDates} />
            <div className="flex flex-wrap items-center gap-2 mt-3 border-t border-[#EBEBEB] pt-3">
              {FLEX_CHIPS.map((c) => (
                <button key={c} type="button" data-testid={`when-flex-${c.replace(/[±\s]/g, "")}`} onClick={() => setFlex(c)} className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${flex === c ? "border-[#222] bg-[#222] text-white" : "border-[#DDD] text-[#222] hover:border-[#222]"}`}>{c}</button>
              ))}
              {dateRange?.from && (
                <button type="button" onClick={() => setDateRange({})} className="ml-auto text-xs text-[#717171] underline">Clear</button>
              )}
            </div>
          </PopoverContent>
        </Popover>

        <div className="hidden md:block h-8 w-px bg-[#DDDDDD]" />

        {/* Who */}
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" data-testid="search-who-trigger" className="flex-1 text-left px-6 py-3 rounded-full hover:bg-[#F7F7F7] transition-colors border-t border-[#EBEBEB] md:border-t-0">
              <div className="text-xs font-semibold text-[#222]">Who</div>
              <div data-testid="search-who-value" className={`text-sm mt-0.5 truncate ${whoLabel !== "Add guests" ? "text-[#222]" : "text-[#717171]"}`}>{whoLabel}</div>
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 rounded-3xl p-4 shadow-xl">
            <div className="divide-y divide-[#EBEBEB]">
              <StepperRow title="Adults" subtitle="Ages 8 years above" value={adults} onChange={setAdults} testid="who-adults" />
              <StepperRow title="Children" subtitle="Ages 3–8" value={children} onChange={setChildren} testid="who-children" />
              <StepperRow title="Infants" subtitle="Under 3" value={infants} onChange={setInfants} testid="who-infants" />
              <StepperRow title="Pets" subtitle="Bringing a friendly pet" value={pets} onChange={setPets} testid="who-pets" />
            </div>
          </PopoverContent>
        </Popover>

        {/* Search button */}
        <div className="p-3 md:p-0 md:pl-2">
          <Button data-testid="search-apply-btn" type="submit" className="w-full md:w-auto rounded-full bg-[#FF385C] hover:bg-[#D90B38] text-white md:h-12 h-11 gap-2 px-5">
            <SearchIcon className="w-5 h-5" />
            <span className="font-semibold">Search</span>
          </Button>
        </div>
      </div>
    </form>
  );
}

export default SearchBar;
